import { List, TextField, DateField, NumberField, useDataProvider } from 'react-admin';
import Datagrid from '../Commons/Datagrid';
import PostPagination from '../Commons/PostPagination';
import { useMediaQuery, createTheme, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import BreadCrumbCustom from '../Commons/BreadCrum';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import historyTracker from '../Commons/historyTracker';
import AuthNotification from '../Commons/AuthNotification';
import { StyleTheme, Style } from '../Commons/Styles.jsx';
import { createStyles, makeStyles } from '@mui/styles';
import FilterSidebar, { ShrinkAwareSelectInput } from '../Commons/FilterSidebar';
import ListActions from '../Commons/ListActions';
import InternalMenu from '../Commons/InternalMenu';
import CircularProgress from '@mui/material/CircularProgress';
// TODO: reemplazar por el ícono definitivo de Bancos cuando esté disponible
import banks from '../Commons/Icons/building.svg';
import requestAuthorization from '../Commons/Icons/colocacion.svg';
// TODO: reemplazar por el ícono definitivo de Imprimir Solicitudes cuando esté disponible
import printRequests from '../Commons/Icons/contabilidad.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

// Por defecto, cuando el listado devuelve 0 filas, react-admin reemplaza TODO el
// <Datagrid> (encabezado incluido) por un mensaje generico sin traducir. Mismo mecanismo
// ya documentado/resuelto en AccountingExpensesList.jsx / ProviderPayableInvoicesList.jsx:
// se arma un reemplazo propio (mismos encabezados que la tabla real) para el prop "empty"
// del Datagrid.
const RequestAuthorizationsEmpty = () => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell style={Style.DataGrid}>Cuenta</TableCell>
        <TableCell style={Style.DataGrid}>Tipo</TableCell>
        <TableCell style={Style.DataGrid}>Moneda</TableCell>
        <TableCell style={Style.DataGrid}>Fecha Documento</TableCell>
        <TableCell style={Style.DataGrid}>Girado</TableCell>
        <TableCell style={Style.DataGrid}>Monto</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        <TableCell colSpan={6} style={{ textAlign: 'center' }}>
          No hay solicitudes pendientes de autorizar.
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

const useStyles = makeStyles(() => createStyles(StyleTheme));

// Listado de solicitudes (cheque/deposito/nota de debito o credito) pendientes de
// autorizar de todas las cuentas bancarias de la empresa (sp_cblistasolicitudesautorizacion,
// via Get_Bank_RequestAuthorizations/Banksapi.cs). Se llega aqui desde "Bancos > Autorizar
// Solicitudes" en el menu de Operaciones. Por ahora es solo de consulta -- las acciones de
// autorizar/rechazar (sp_cbautorizar_solicitud/sp_cbrechaza_solicitud) no se pidieron para
// esta pantalla todavia.
const RequestAuthorizationsList = (props) => {
  const classes = useStyles();
  const theme = createTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [openAuth, setOpenAuth] = useState(false);
  const [auth, setAuth] = useState(false);
  const [movementTypeChoices, setMovementTypeChoices] = useState([]);
  const [currencyChoices, setCurrencyChoices] = useState([]);
  // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
  // "initialLoading" en el return): sin este estado la ventana se veia en blanco
  // (solo el breadcrumb) mientras se traian los permisos y los catalogos de filtros.
  const [initialLoading, setInitialLoading] = useState(true);
  const dataProvider = useDataProvider();

  useEffect(() => {
    (async () => {
      try {
        const currentPath = location.pathname;
        historyTracker.addPath(currentPath, 'Autorizar Solicitudes', 2);

        // Recurso de permisos nuevo ("BankRequestAuthorization"): requiere que se le den de
        // alta los permisos correspondientes en Auth0 (via Auth/CreatePermissions o el panel
        // de Auth0) antes de que cualquier usuario pueda ver esta pantalla.
        const authdata = { data: hasPermissions('BankRequestAuthorization', 'list') };
        if (authdata.data.length > 0) {
          setAuth(authdata.data[0]);
          setOpenAuth(!authdata.data[0]);

          if (authdata.data[0]) {
            // Catalogos para los filtros: sp_lista_cbtmovban (tipos de movimiento bancario,
            // via Get_Bank_MovementTypes) y sp_lista_monedas (via el recurso "currency" ya
            // existente, reutilizado igual que en ProviderInvoiceCreate/ProviderPayableInvoiceShow).
            const [movementTypesData, currenciesData] = await Promise.all([
              dataProvider.getList('bank/movementtype', { filter: {}, pagination: {}, sort: {} }).catch(() => ({ data: [] })),
              dataProvider.getList('currency', { filter: {}, pagination: {}, sort: {} }).catch(() => ({ data: [] })),
            ]);
            setMovementTypeChoices(movementTypesData.data);
            setCurrencyChoices(currenciesData.data);
          }
        } else {
          setAuth(false);
          setOpenAuth(true)
        }
      }
      catch {
      }
      finally {
        setInitialLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, location.pathname]);

  // sp_cblistasolicitudesautorizacion no soporta filtrar por tipo de movimiento (el
  // parametro @codigo_tipo_mov existe pero esta comentado en la propia SP) ni por moneda
  // (no tiene parametro para eso); ambos filtros se resuelven en memoria en
  // Get_Bank_RequestAuthorizations/Banksapi.cs, igual que netPaymentFrom/To en
  // Get_Provider_PayableInvoices.
  const PostFilter = [
    <ShrinkAwareSelectInput InputProps={{ disableUnderline: true }} label={<span>{"Tipo"}</span>} source="movementType" choices={movementTypeChoices} alwaysOn />,
    <ShrinkAwareSelectInput InputProps={{ disableUnderline: true }} label={<span>{"Moneda"}</span>} source="currency" choices={currencyChoices} alwaysOn />,
  ];

  const handleRedirectRequestAuthorizations = () => {
    navigate(`/bank/requestauthorization`);
  };

  // Bancos > Imprimir Solicitudes: por ahora solo navega el menú (mismo criterio que se
  // uso para Autorizar Solicitudes antes de construir su pantalla); la vista real se
  // conecta cuando exista el recurso correspondiente en el API y el Resource en App.jsx.
  const handleRedirectPrintRequests = () => {
    navigate(`/bank/printrequests`);
  };

  const [openBanks, setOpenBanks] = useState(true);

  const handleClickOpenBanks = () => {
    setOpenBanks(!openBanks);
  };

  const menus = [{
    icon: <img src={banks} alt="logo" style={Style.MenuLogo} />, name: 'Bancos',
    open: openBanks,
    openAction: handleClickOpenBanks,
    actions: [
      { icon: <img src={requestAuthorization} alt="logo" style={Style.MenuLogo} />, name: 'Autorizar Solicitudes', onClick: handleRedirectRequestAuthorizations },
      { icon: <img src={printRequests} alt="logo" style={Style.MenuLogo} />, name: 'Imprimir Solicitudes', onClick: handleRedirectPrintRequests },
    ]
  }];

  let pagination = <PostPagination menu={<InternalMenu menus={menus} />} />;

  return (
    <div class="container">
      <BreadCrumbCustom history={historyTracker.getHistory()} />
      {initialLoading ? (
        // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y
        // el mismo CircularProgress que ya se usa en otras pantallas para el
        // guardado, pero aqui cubre la espera de auth + catalogos de filtros, que
        // antes dejaba la pantalla en blanco.
        <div class="Loading" style={{ display: 'block' }}>
          <CircularProgress size="15rem" />
        </div>
      ) : auth ? (
        <div class="row">
          <div class="col-12">
            {isSmall ? (
              <List empty={false} {...props} actions={<ListActions download />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props} empty={false}>
                </Datagrid>
              </List>
            ) : (
              <List empty={false} perPage={10} {...props} aside={<FilterSidebar {...props} filters={PostFilter} />} actions={<ListActions download />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props} rowClick="show" style={Style.DataGrid} empty={<RequestAuthorizationsEmpty />}>
                  <TextField source="accountNumber" style={Style.fieldList} label="Cuenta" cellClassName={classes.fields} />
                  <TextField source="movementTypeDescription" style={Style.fieldList} label="Tipo" cellClassName={classes.fields} />
                  <TextField source="currencyName" style={Style.fieldList} label="Moneda" cellClassName={classes.fields} />
                  <DateField source="documentDate" style={Style.fieldList} label="Fecha Documento" cellClassName={classes.fields} />
                  <TextField source="payableTo" style={Style.fieldList} label="Girado" cellClassName={classes.fields} />
                  <NumberField source="amount" style={Style.fieldList} label="Monto" cellClassName={classes.fields} options={{ style: 'decimal', minimumFractionDigits: 2 }} />
                </Datagrid>
              </List>
            )}
          </div>
        </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)}
    </div>
  )
};

export default RequestAuthorizationsList;
